// src/pages/Dashboard/hooks/useDashboardJurisprudence.js

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Hook customizado para gerenciar jurisprudências relacionadas ao caso
 * Encapsula a lógica de carregamento e remoção de jurisprudências
 * 
 * @param {number} idCaso - ID do caso para buscar jurisprudências
 * @returns {object} - Estado e funções para gerenciar jurisprudências
 */
export const useDashboardJurisprudence = (idCaso) => {
  const [jurisprudenciasRelacionadas, setJurisprudenciasRelacionadas] = useState([]);
  const [loadingJurisprudencias, setLoadingJurisprudencias] = useState(false);
  const [jurisprudenciaSelecionada, setJurisprudenciaSelecionada] = useState(null);

  /**
   * Carrega jurisprudências relacionadas ao caso
   */
  const carregarJurisprudenciasRelacionadas = useCallback(async () => {
    if (!idCaso) {
      setJurisprudenciasRelacionadas([]);
      return;
    }

    try {
      setLoadingJurisprudencias(true);
      const response = await axios.get(`/api/jurisprudencia/caso/${idCaso}/relacionadas`);
      if (response.data.success) {
        setJurisprudenciasRelacionadas(response.data.data);
        console.log('✅ Jurisprudências carregadas:', response.data.data.length);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar jurisprudências relacionadas:', error);
      setJurisprudenciasRelacionadas([]);
    } finally {
      setLoadingJurisprudencias(false);
    }
  }, [idCaso]);

  /**
   * Remove uma jurisprudência relacionada
   * @param {number} idJurisprudencia - ID da jurisprudência a remover
   * @returns {Promise<boolean>} - true se removido com sucesso
   */
  const removerJurisprudencia = useCallback(async (idJurisprudencia) => {
    if (!idCaso) return false;
    
    if (!window.confirm('Deseja remover esta jurisprudência relacionada?')) {
      return false;
    }

    try {
      const response = await axios.delete(
        `/api/jurisprudencia/caso/${idCaso}/remover/${idJurisprudencia}`
      );
      
      if (response.data.success) {
        // Recarregar jurisprudências
        await carregarJurisprudenciasRelacionadas();
        console.log('✅ Jurisprudência removida com sucesso');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Erro ao remover jurisprudência:', error);
      throw error;
    }
  }, [idCaso, carregarJurisprudenciasRelacionadas]);

  /**
   * Define a jurisprudência selecionada para visualização de detalhes
   * @param {object|null} jurisprudencia - Jurisprudência a selecionar ou null para desselecionar
   */
  const selecionarJurisprudencia = useCallback((jurisprudencia) => {
    setJurisprudenciaSelecionada(jurisprudencia);
  }, []);

  /**
   * Limpa a jurisprudência selecionada
   */
  const limparJurisprudenciaSelecionada = useCallback(() => {
    setJurisprudenciaSelecionada(null);
  }, []);

  // Carregar jurisprudências quando o ID do caso mudar
  useEffect(() => {
    carregarJurisprudenciasRelacionadas();
  }, [carregarJurisprudenciasRelacionadas]);

  return {
    // Estado
    jurisprudenciasRelacionadas,
    loadingJurisprudencias,
    jurisprudenciaSelecionada,
    
    // Funções
    carregarJurisprudenciasRelacionadas,
    removerJurisprudencia,
    selecionarJurisprudencia,
    limparJurisprudenciaSelecionada
  };
};